import axios from "axios";
import { toast } from "react-hot-toast";
import { createTrayStore } from "../utilities/createTrayStore";
import { DOCUMENTS_API, STAGE_LABELS } from "../components/Agency/AgencyDocuments/api";

// Uploading an agency policy/statute document and following the server's
// ingest stages (reading pages, splitting sections, tagging, indexing) in the
// bottom-right tray. A tiny store (not React state) so the upload keeps
// running while the admin moves between tabs. Same shape as formUploadService.

// Lists of documents listen for this to refresh.
export const DOCUMENTS_CHANGED_EVENT = "pnai:agency-documents-changed";

export type DocumentUploadItem = {
  id: string;
  name: string;
  status: "uploading" | "processing" | "done" | "error";
  progress: number;
  after: string;
  error?: string;
  openPath?: string;
};

const store = createTrayStore<DocumentUploadItem>(["uploading", "processing"]);
const patch = store.patch;

export const subscribeDocumentUploads = store.subscribe;
export const getDocumentUploads = store.get;
export const clearFinishedDocumentUploads = store.clearFinished;

const controllers = new Map<string, AbortController>();

export function cancelDocumentUpload(id: string): void {
  const item = store.get().find((i) => i.id === id);
  if (!item || item.status !== "uploading") return;
  controllers.get(id)?.abort();
  store.remove(id);
}

export interface StartDocumentUploadParams {
  file: File;
  title: string;
  docType: "policy" | "statute" | "other";
  presetTagIds?: string[];
  openPath: (documentId: string) => string;
}

export function startDocumentUpload(params: StartDocumentUploadParams): string {
  const id = `doc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const controller = new AbortController();
  controllers.set(id, controller);
  store.add({ id, name: params.title, status: "uploading", progress: 0, after: "" });
  void run(id, params, controller);
  return id;
}

const POLL_MS = 4000;
const POLL_LIMIT_MS = 30 * 60_000;

async function run(id: string, p: StartDocumentUploadParams, controller: AbortController) {
  let documentId: string | undefined;
  try {
    const { data: presign } = await axios.post(`${DOCUMENTS_API}/get-upload-url`, { fileName: p.file.name, fileType: p.file.type, fileSize: p.file.size });
    await axios.put(presign.uploadURL, p.file, {
      headers: { "Content-Type": presign.contentType || p.file.type || "application/octet-stream" },
      signal: controller.signal,
      onUploadProgress: (e) => patch(id, { progress: e.total ? Math.round((e.loaded * 100) / e.total) : 0 }),
    });
    controllers.delete(id);

    patch(id, { status: "processing", progress: 100, after: STAGE_LABELS.extract });
    const { data } = await axios.post(DOCUMENTS_API, {
      title: p.title,
      s3Key: presign.key,
      docType: p.docType,
      fileName: p.file.name,
      fileSize: p.file.size,
      presetTagIds: p.presetTagIds,
    });
    documentId = data.document.id as string;
    window.dispatchEvent(new Event(DOCUMENTS_CHANGED_EVENT));

    // Follow the stages until the document is ready or failed.
    const started = Date.now();
    while (Date.now() - started < POLL_LIMIT_MS) {
      await new Promise((r) => setTimeout(r, POLL_MS));
      const { data: detail } = await axios.get(`${DOCUMENTS_API}/${documentId}`);
      const doc = detail.document;
      if (doc.status === "completed") {
        patch(id, { status: "done", after: `${doc.section_count || 0} sections tagged and indexed`, openPath: p.openPath(documentId) });
        toast.success(`${p.title} is ready`);
        break;
      }
      if (doc.status === "failed") {
        patch(id, { status: "error", error: doc.error_message || "Processing failed", openPath: p.openPath(documentId) });
        toast.error(`${p.title} could not be processed`);
        break;
      }
      patch(id, { after: STAGE_LABELS[doc.stage] || doc.stage });
    }
  } catch (error: any) {
    if (!controller.signal.aborted) {
      console.error(`Failed to upload document ${p.title}:`, error);
      patch(id, { status: "error", error: error?.response?.data?.error || "Upload failed" });
      toast.error(error?.response?.data?.error || "Failed to upload document");
    }
  } finally {
    controllers.delete(id);
    if (documentId) window.dispatchEvent(new Event(DOCUMENTS_CHANGED_EVENT));
  }
}
